import React from 'react';
import { Box, Container, Typography, Button, AppBar, Toolbar } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import nzImage from '../assets/nz.jpg';

const Home = () => {
  const navigate = useNavigate();

  // Add global reset styles to remove default margins/padding
  React.useEffect(() => {
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.documentElement.style.margin = '0';
    document.documentElement.style.padding = '0';
  }, []);

  return (
    <Box sx={{ 
      margin: 0, 
      padding: 0, 
      width: '100vw',
      minHeight: '100vh',
      overflow: 'hidden'
    }}>
      {/* Navigation Bar */}
      <AppBar 
        position="static" 
        sx={{
          backgroundColor: '#051b14', // Deep Forest Shadow - darker than before
          backdropFilter: 'blur(10px)',
          boxShadow: '0 2px 20px rgba(0,0,0,0.3)',
          borderBottom: '1px solid rgba(255,255,255,0.1)'
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 400,
                color: 'white',
                fontFamily: '"Libre Baskerville", "Georgia", serif',
                fontSize: '1.8rem',
                letterSpacing: '0.5px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  color: '#c7d2cc',
                  transform: 'translateY(-1px)',
                  textShadow: '0 2px 8px rgba(255,255,255,0.2)'
                }
              }}
              onClick={() => navigate('/')}
            >
              Heather Walker Photography
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                sx={{ 
                  color: 'white',
                  fontWeight: 500,
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: '#90caf9'
                  }
                }}
                onClick={() => navigate('/portfolio')}
              >
                Portfolio
              </Button>
              <Button 
                sx={{ 
                  color: 'white',
                  fontWeight: 500,
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: '#90caf9'
                  }
                }}
                onClick={() => navigate('/contact')}
              >
                Contact
              </Button>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Hero Section - Full Screen */}
      <Box
        sx={{
          backgroundImage: `url(${nzImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          backgroundAttachment: 'fixed', // Keeps image fixed during scroll
          minHeight: '100vh', // Full viewport height
          height: 'auto',
          width: '100vw',
          margin: 0,
          padding: 0,
          paddingTop: '80px', // Add padding instead of subtracting from height
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden', // Prevents any overflow issues
          // Responsive adjustments
          '@media (max-width: 768px)': {
            minHeight: '100vh',
            paddingTop: '70px',
            backgroundAttachment: 'scroll', // Better performance on mobile
          },
          '@media (max-width: 480px)': {
            minHeight: '100vh',
            paddingTop: '60px',
            backgroundAttachment: 'scroll',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.4)', // Dark overlay
            zIndex: 1,
          },
        }}
      >
        <Container 
          maxWidth="lg" 
          sx={{ 
            position: 'relative', 
            zIndex: 2,
            px: { xs: 2, sm: 3, md: 4 }, // Responsive padding
          }}
        >
          <Typography 
            variant="h1"
            sx={{
              fontWeight: 300,
              fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem', lg: '5rem' },
              fontFamily: '"Cormorant Garamond", serif',
              letterSpacing: '1px',
              lineHeight: 1.2,
              textShadow: '2px 2px 8px rgba(0,0,0,0.7)',
              mb: 2,
            }}
          >
            Real moments. Raw beauty.
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              mt: 2, 
              mb: 4,
              fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
              fontWeight: 300,
              letterSpacing: '0.5px',
              textShadow: '1px 1px 4px rgba(0,0,0,0.7)',
              maxWidth: '600px',
              mx: 'auto',
              px: { xs: 2, md: 0 },
            }}
          >
            Emotional, authentic, and beautifully curated photography
          </Typography>
          <Button 
            variant="contained" 
            size="large"
            onClick={() => navigate('/contact')}
            sx={{
              backgroundColor: '#1976d2', // Classic conversion-optimized blue
              color: 'white',
              fontSize: { xs: '1rem', md: '1.1rem' },
              px: { xs: 3, md: 4 },
              py: { xs: 1.5, md: 2 },
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: '0 4px 20px rgba(25, 118, 210, 0.4)', // Blue shadow
              '&:hover': {
                backgroundColor: '#1565c0', // Deeper blue on hover
                boxShadow: '0 6px 25px rgba(25, 118, 210, 0.6)',
                transform: 'translateY(-2px)',
              },
              '&:active': {
                backgroundColor: '#0d47a1', // Darkest blue when clicked
              },
            }}
          >
            Book Now
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default Home